import { roundMoney, type ReceiptView } from 'shared-models/browser';

// Splits a multi-payment receipt into one receipt per payment line (each its own fiscal copy).
// Each copy's totals are recomputed for that payment slice so the VAT split matches its own amount
// (carrying the full-order totals would print the whole-order VAT split next to a single payment).
export function splitReceiptByPayment(receipt: ReceiptView): ReceiptView[] {
  if (receipt.payments.length <= 1) {
    return [receipt];
  }
  const rate = receipt.totals.vatRatePercent;
  return receipt.payments.map((payment, index) => {
    const baseAmount = payment.baseAmount ?? payment.amount;
    const tipAmount = payment.tipAmount ?? 0;
    const exVatTotal = roundMoney(rate > 0 ? baseAmount / (1 + rate / 100) : baseAmount, receipt.roundAmounts ?? false);
    return {
      ...receipt,
      payments: [payment],
      isPartial: true,
      paymentIndex: index + 1,
      paymentCount: receipt.payments.length,
      totals: {
        ...receipt.totals,
        exVatTotal,
        vatAmount: roundMoney(baseAmount - exVatTotal, receipt.roundAmounts ?? false),
        billableTotal: baseAmount,
        tipAmount,
        paidAmount: payment.amount,
        changeAmount: 0,
      },
    };
  });
}

export type ReceiptPrintJob = {
  id: string;
  receipt: ReceiptView;
  title?: string;
  showPaymentDetails?: boolean;
};

// Print list: N partial copies, plus one full "חשבון" summary when paid via >1 payment.
export function receiptsForPrinting(receipt: ReceiptView): ReceiptPrintJob[] {
  const jobs: ReceiptPrintJob[] = splitReceiptByPayment(receipt).map((r, index) => ({
    id: r.payments[0] ? `payment:${r.payments[0].paymentlineId}` : `payment:${index}`,
    receipt: r,
  }));
  if (receipt.payments.length > 1) {
    jobs.push({ id: 'final', receipt, title: 'חשבון', showPaymentDetails: false });
  }
  return jobs;
}

export const SUMMARY_JOB_ID = 'summary';

// Every job a caller may select by id: the default list plus the whole-order fiscal copy with its
// payment block (what the tablet/PC close preview shows). Not part of the default print run.
export function printableJobs(receipt: ReceiptView): ReceiptPrintJob[] {
  const jobs = receiptsForPrinting(receipt);
  return receipt.payments.length > 1
    ? [...jobs, { id: SUMMARY_JOB_ID, receipt, showPaymentDetails: true }]
    : jobs;
}

// Ids that print exactly one summarising receipt; a single-payment order's only copy already is one.
export function summaryJobIds(receipt: ReceiptView): string[] {
  return receipt.payments.length > 1 ? [SUMMARY_JOB_ID] : receiptsForPrinting(receipt).slice(0, 1).map((job) => job.id);
}
